import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { V4HomeAccountSlot } from './V4HomeAccountSlot';
import { V4SignInModal } from './V4SignInModal';
import { V4CaptureSheet } from './V4CaptureSheet';
import { useV4AccountState } from './useV4AccountState';
import { useAccountMenuSurface } from './useAccountMenuSurface';
import { hasBoard } from './accountState';

/** Account entry for the site header. The avatar menu and login form share one surface state. */

const Placeholder = styled.span`
  display: inline-block;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${({ theme }) => theme.color.creamLight};
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
`;

function hasMatchMedia(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => hasMatchMedia() && window.matchMedia('(min-width: 1024px)').matches,
  );
  useEffect(() => {
    if (!hasMatchMedia()) return undefined; // jsdom / SSR — leave the seed value
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return isDesktop;
}

export interface V4HomeDoorProps {
  /** Override the breakpoint (host/tests/stories). Defaults to a live matchMedia. */
  desktop?: boolean;
  /**
   * When the HOST owns a single shared sign-in modal — the homepage does, so its
   * header door and its sticky-footer "Sign in" open ONE modal — it passes the
   * opener here and this door defers to it, rendering no modal of its own.
   * Calendar/Quiz/Gift-Guides mount the door standalone and omit this, keeping the
   * local modal.
   */
  onRequestSignIn?: () => void;
}

export const V4HomeDoor: React.FC<V4HomeDoorProps> = ({ desktop, onRequestSignIn }) => {
  const { state, ready } = useV4AccountState();
  const detectedDesktop = useIsDesktop();
  const isDesktop = desktop ?? detectedDesktop;

  const { surface, toggleMenu, closeMenu, openSheet, openSignIn, close } = useAccountMenuSurface({
    signedIn: state.signedIn,
    captureConfirmed: false,
  });

  // Sign-in has two ownership modes:
  //  • INJECTED (LandingPage): the external modal owns the render AND its own focus
  //    lifecycle (`useDialogA11y`). We must NOT hold our surface at 'sign-in' — we
  //    render no modal for it, so it would strand as stale state and block focus
  //    restoration. Close our menu/sheet (surface → 'none', which returns focus to
  //    the avatar) THEN open the external modal, which captures that avatar as its
  //    restore target and returns focus to it on dismiss.
  //  • STANDALONE (Calendar/Quiz/Gift-Guides): the local modal below renders on
  //    `surface === 'sign-in'`, so drive the surface machine.
  const chooseSignIn = useCallback(() => {
    if (onRequestSignIn) {
      close();
      onRequestSignIn();
    } else {
      openSignIn();
    }
  }, [close, openSignIn, onRequestSignIn]);

  // Home-menu eligibility ALSO depends on desktop + has-board (via `homeAccountSlot`),
  // which the shared hook's signed-in / confirmed reconciliation doesn't cover. If an
  // open menu loses its last board or the viewport drops below 1024px, the slot
  // unmounts `V4AccountMenu` — so close the surface too, or it could silently reopen
  // when eligibility returns, or strand keyboard focus in a removed subtree.
  useEffect(() => {
    const menuEligible = isDesktop && !state.signedIn && hasBoard(state);
    if (surface === 'account-menu' && !menuEligible) close();
  }, [isDesktop, state, surface, close]);

  if (!ready) return <Placeholder aria-hidden data-testid="v4-home-placeholder" />;

  return (
    <>
      <V4HomeAccountSlot
        state={state}
        desktop={isDesktop}
        // The `sign-in` and `sent` slot states (no-board) route their own Sign-in
        // through the surface machine too, so there is one modal instance.
        onSignIn={chooseSignIn}
        onOpenAccount={chooseSignIn}
        // Anon-with-a-board desktop avatar → the nested account menu.
        accountMenu={{
          surface,
          onToggleMenu: toggleMenu,
          onCloseMenu: closeMenu,
          onOpenCapture: openSheet,
          onSignIn: chooseSignIn,
        }}
      />
      <V4CaptureSheet
        open={surface === 'capture-sheet'}
        boards={state.boards}
        browser={state.browser}
        onDismiss={() => close()}
        onSignedIn={() => close()}
      />
      {onRequestSignIn ? null : (
        <V4SignInModal
          open={surface === 'sign-in'}
          onClose={() => close()}
          browser={state.browser}
          intent="signin"
          boardCount={state.boards.length}
          onSignedIn={() => close()}
        />
      )}
    </>
  );
};

export default V4HomeDoor;
